import * as React from 'react';
import {FormControl, MenuItem, Select, type SelectChangeEvent} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {usePeriod, ALL_BRANCHES_VALUE} from '../../context/PeriodContext';
import {formatPeriodLabel} from '../../utils/periodLabel';

/**
 * Global period + branch filters, rendered in the top toolbar (via
 * DashboardToolbarActions) so every page shares the same selection.
 * Branch supports an "All branches" option — see PeriodContext for why this
 * is kept separate from `selectedBranch` (which several pages require to be a
 * single, specific branch id).
 */
export function PeriodBranchToolbarSelector(): React.ReactElement | null {
    const {t} = useTranslation();
    const {
        allPeriods, branches, optionsLoading,
        selectedPeriod, selectedPeriodValue, setPeriodAndValue,
        dashboardBranch, setDashboardBranch,
    } = usePeriod();

    if (optionsLoading && allPeriods.length === 0 && branches.length === 0) {
        return null;
    }

    const handlePeriodChange = (event: SelectChangeEvent) => {
        const period = allPeriods.find((p) => p.periodKey === event.target.value);
        setPeriodAndValue(period?.periodKey ?? null, period?.value ?? null);
    };

    const handleBranchChange = (event: SelectChangeEvent) => {
        const branchId = event.target.value;
        const branchName = branchId === ALL_BRANCHES_VALUE
            ? t('common.filters.all')
            : branches.find((b) => b.id === branchId)?.name ?? '';
        setDashboardBranch(branchId, branchName);
    };

    return (
        <>
            <FormControl size="small" sx={{minWidth: 150}}>
                <Select
                    displayEmpty
                    value={selectedPeriod ?? ''}
                    onChange={handlePeriodChange}
                    renderValue={() => selectedPeriod
                        ? formatPeriodLabel(t, selectedPeriod, selectedPeriodValue)
                        : t('common.filters.selectPeriod')}
                >
                    {allPeriods.map((period) => (
                        <MenuItem key={period.periodKey} value={period.periodKey}>
                            {formatPeriodLabel(t, period.periodKey, period.value)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" sx={{minWidth: 150}}>
                <Select value={dashboardBranch} onChange={handleBranchChange}>
                    <MenuItem value={ALL_BRANCHES_VALUE}>{t('common.filters.all')}</MenuItem>
                    {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    );
}
